import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "submenu"]
  static values = { transitionDuration: { type: Number, default: 220 } }

  connect() {
    if (!this.hasTriggerTarget || !this.hasSubmenuTarget) return
    this.ready = true

    this.triggerEl = this.triggerTarget
    this.submenuEl = this.submenuTarget
    this.originalParent = this.submenuEl.parentNode
    this.originalNextSibling = this.submenuEl.nextSibling

    this.submenuEl.hidden = true
    this.submenuEl.setAttribute("aria-hidden", "true")
    document.body.appendChild(this.submenuEl)

    this.boundDropdownHide = this.close.bind(this)
    this.boundDocumentClick = this.onDocumentClick.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    this.boundReposition = this.positionSubmenu.bind(this)
    this.boundBeforeCache = this.beforeCache.bind(this)

    this.element.addEventListener("hidden.bs.dropdown", this.boundDropdownHide)
    document.addEventListener("click", this.boundDocumentClick)
    document.addEventListener("keydown", this.boundKeydown)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    window.addEventListener("resize", this.boundReposition)
    window.addEventListener("scroll", this.boundReposition, true)
  }

  disconnect() {
    if (!this.ready) return
    this.ready = false

    this.close()
    clearTimeout(this.closeTimer)

    this.element.removeEventListener("hidden.bs.dropdown", this.boundDropdownHide)
    document.removeEventListener("click", this.boundDocumentClick)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)

    this.restoreSubmenu()
  }

  // -- actions -----------------------------------------------------------------

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.ready) return

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    clearTimeout(this.closeTimer)
    this.isOpen = true
    this.triggerEl.setAttribute("aria-expanded", "true")
    this.submenuEl.hidden = false
    this.submenuEl.setAttribute("aria-hidden", "false")
    this.submenuEl.style.display = "block"
    this.submenuEl.classList.remove("is-closing")
    requestAnimationFrame(() => {
      this.positionSubmenu()
      this.submenuEl.classList.add("is-open")
    })
  }

  close() {
    clearTimeout(this.closeTimer)
    this.isOpen = false
    if (this.triggerEl) {
      this.triggerEl.setAttribute("aria-expanded", "false")
    }
    if (this.submenuEl) {
      this.submenuEl.setAttribute("aria-hidden", "true")
      this.submenuEl.classList.remove("is-open")
      this.submenuEl.classList.add("is-closing")
      this.closeTimer = window.setTimeout(() => {
        this.submenuEl.hidden = true
        this.submenuEl.classList.remove("is-closing", "submenu--opens-left", "submenu--opens-right")
        this.submenuEl.style.display = ""
        this.submenuEl.style.removeProperty("top")
        this.submenuEl.style.removeProperty("left")
      }, this.transitionDurationValue)
    }
  }

  positionSubmenu() {
    if (!this.isOpen) return

    const gap = 8
    const margin = 12
    const triggerRect = this.triggerEl.getBoundingClientRect()
    const submenuRect = this.submenuEl.getBoundingClientRect()

    let left = triggerRect.right + gap
    let opensLeft = false
    if (left + submenuRect.width > window.innerWidth - margin) {
      left = triggerRect.left - submenuRect.width - gap
      opensLeft = true
    }
    left = Math.max(margin, left)

    const maxTop = Math.max(margin, window.innerHeight - submenuRect.height - margin)
    const preferredTop = triggerRect.top - 8
    const top = Math.min(Math.max(margin, preferredTop), maxTop)

    this.submenuEl.style.top = `${top}px`
    this.submenuEl.style.left = `${left}px`
    this.submenuEl.classList.toggle("submenu--opens-left", opensLeft)
    this.submenuEl.classList.toggle("submenu--opens-right", !opensLeft)
  }

  // -- private -----------------------------------------------------------------

  beforeCache() {
    this.close()
    clearTimeout(this.closeTimer)
    this.restoreSubmenu()
  }

  restoreSubmenu() {
    if (!this.submenuEl || !this.originalParent) return

    if (this.originalNextSibling?.parentNode === this.originalParent) {
      this.originalParent.insertBefore(this.submenuEl, this.originalNextSibling)
    } else {
      this.originalParent.appendChild(this.submenuEl)
    }
  }

  onDocumentClick(event) {
    if (!this.isOpen) return
    if (this.triggerEl.contains(event.target) || this.submenuEl.contains(event.target)) return
    this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }
}
